import { Dispatch, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DocStatus, DocTypes } from 'components/DocBox/DocBox.types';
import { STATUS } from 'constants/constants';
import { ApplicationDetailsActionTypes } from 'datasource/useApplicationDetails/ApplicationDetailsManager.types';
import { docLoadingEntity, getNoDocumentEntry } from 'datasource/useApplicationDetails/constants';

type UseDerivedApplicationDocumentsParams = {
    dispatch: Dispatch<any>;
    generalDocumentsData: any;
    proofOfAssetsDocsData: any;
    documentDataForOldAdvisory: any;
    isJA?: boolean;
    isProofOfAssetsDocsDataLoading?: boolean;
    isGetGeneralDocumentsLoading?: boolean;
    language: string;
};

/**
 * Derives proof-of-assets and basic-agreement document lists from the fetched general /
 * proof-of-assets data and pushes them into the reducer. Also computes the advisory document.
 * Extracted verbatim from ApplicationDetailsManager (dep arrays preserved).
 */
export const useDerivedApplicationDocuments = ({
    dispatch,
    generalDocumentsData,
    proofOfAssetsDocsData,
    documentDataForOldAdvisory,
    isJA,
    isProofOfAssetsDocsDataLoading,
    isGetGeneralDocumentsLoading,
    language,
}: UseDerivedApplicationDocumentsParams) => {
    const { t } = useTranslation();

    useEffect(() => {
        if (isProofOfAssetsDocsDataLoading || isGetGeneralDocumentsLoading) return;
        let proofOfAssetDocuments = [];

        if (generalDocumentsData && proofOfAssetsDocsData) {
            proofOfAssetDocuments = proofOfAssetsDocsData?.proofOfAssetsDocumentInfo?.reduce((docs, client) => {
                const generalContactData = generalDocumentsData?.contactDocuments?.find((clientItem) => (client.contactId === clientItem.contactId));

                client?.proofOfAssetDocuments?.forEach(({
                    documentName,
                    originOfAsset,
                }) => {
                    const generalDoc = generalContactData?.proofOfAssetDocuments?.find((docItem) => docItem.originOfAsset === originOfAsset);
                    let documentNameToSet = documentName;

                    if (isJA) {
                        const splittedDocumentName = documentName.split(':');

                        documentNameToSet = `${splittedDocumentName?.[0]} ${client?.firstName} ${client?.lastName}: ${splittedDocumentName?.[1]}`;
                    }

                    docs.push({
                        contactId: client?.contactId,
                        documentName: documentNameToSet,
                        status: generalDoc ? DocStatus.ReadyToSubmit : DocStatus.ReadyToUpload,
                        documentId: generalDoc ? generalDoc?.documentId : undefined,
                        originOfAsset,
                        type: DocTypes.PROOF_OF_ASSETS,
                    });
                });

                return docs;
            }, []);
        }

        dispatch({
            type: ApplicationDetailsActionTypes.SET_PROOF_OF_ASSETS_DOCS,
            payload: proofOfAssetDocuments?.length ? proofOfAssetDocuments : [getNoDocumentEntry({ documentName: t('ApplicationDetails.AdvisorySection.ProofOfAssets.notAvailableName') })],
        });
    }, [proofOfAssetsDocsData, generalDocumentsData, isJA, language]);

    useEffect(() => {
        if (isGetGeneralDocumentsLoading) return;
        let basicAgreementDocuments = [];

        if (generalDocumentsData?.contactDocuments) {
            basicAgreementDocuments = generalDocumentsData?.contactDocuments?.reduce((docs, client) => {
                if (!client?.basicAgreementDocument) return docs;
                let documentName = client?.basicAgreementDocument?.documentName || client?.basicAgreementDocument?.fileName;

                documentName = isJA ? `${documentName} ${client?.firstName} ${client?.lastName}` : documentName;

                docs.push({
                    documentName,
                    status: client?.basicAgreementDocument?.signStatus === STATUS.submitted ? DocStatus?.ReadyToSubmit : DocStatus?.ReadyToSign,
                    documentId: client?.basicAgreementDocument?.documentId,
                    contactId: client?.contactId,
                });

                return docs;
            }, []);
        }

        const noDocEntry = getNoDocumentEntry({ documentName: t('ApplicationDetails.AdvisorySection.BasicAgreement.notAvailableName') });
        const noDocs = isJA ? [noDocEntry, noDocEntry] : [noDocEntry];

        dispatch({
            type: ApplicationDetailsActionTypes.SET_BASIC_AGREEMENT_DOCS,
            payload: basicAgreementDocuments?.length
                ? basicAgreementDocuments
                : noDocs,
        });
    }, [generalDocumentsData?.contactDocuments, isJA]);

    const advisoryDocument = useMemo(() => ((generalDocumentsData?.advisoryDocument !== undefined || documentDataForOldAdvisory !== undefined) ? {
        documentName: generalDocumentsData?.advisoryDocument?.documentName || documentDataForOldAdvisory?.fileName,
        status: (generalDocumentsData?.advisoryDocument?.documentId || documentDataForOldAdvisory?.documentId) ? DocStatus?.ReadyToSubmit : DocStatus?.Error,
        documentId: generalDocumentsData?.advisoryDocument?.documentId || documentDataForOldAdvisory?.documentId,
        type: DocTypes.ADVISORY,
    } : docLoadingEntity), [generalDocumentsData?.advisoryDocument, documentDataForOldAdvisory]);

    return { advisoryDocument };
};
